import { useCallback, useContext } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { BillBookletModal } from "@features/BillBookletModal/BillBookletModal";
import { BillCarryOverModal } from "@features/BillCarryOverModal/BillCarryOverModal";
import { BillDetailizationModal } from "@features/BillDetailizationModal/BillDetailizationModal";
import { EditServiceProviderSettingsModal } from "@features/EditServiceProviderSettingsModal/EditServiceProviderSettingsModal";
import { CalculatedAmount } from "@features/JointBillList/components/JointBillPeriod/components/JointBill/components/JointBillTable/components/CalculatedAmount/CalculatedAmount";
import { BillDetails } from "@features/JointBillList/components/JointBillPeriod/components/JointBill/components/JointBillTable/components/ProviderBill/components/BillDetails/BillDetails";
import { BillNumberForm } from "@features/JointBillList/components/JointBillPeriod/components/JointBill/components/JointBillTable/components/ProviderBill/components/BillNumberForm/BillNumberForm";
import { ProviderBillNameCell } from "@features/JointBillList/components/JointBillPeriod/components/JointBill/components/JointBillTable/components/ProviderBill/components/ProviderBillNameCell/ProviderBillNameCell";
import { ProviderBillStatusBadge } from "@features/JointBillList/components/JointBillPeriod/components/JointBill/components/JointBillTable/components/ProviderBill/components/ProviderBillStatusBadge/ProviderBillStatusBadge";
import { ProviderBillTotalCell } from "@features/JointBillList/components/JointBillPeriod/components/JointBill/components/JointBillTable/components/ProviderBill/components/ProviderBillTotalCell/ProviderBillTotalCell";
import { RemoveServiceProviderModal } from "@features/JointBillList/components/JointBillPeriod/components/JointBill/components/JointBillTable/components/ProviderBill/components/RemoveServiceProviderModal/RemoveServiceProviderModal";
import { JointBillTableRowLayout } from "@features/JointBillList/components/JointBillPeriod/components/JointBill/layouts/JointBillTableRowLayout/JointBillTableRowLayout";
import { JointBillContext } from "@features/JointBillList/utils";
import { TransferServiceProviderModal } from "@features/TransferServiceProviderModal/TransferServiceProviderModal";
import { useApiErrorUtils } from "@hooks/useApiErrorUtils";
import { useFeatureConfig } from "@hooks/useFeatureFlags";
import { useModalStore } from "@store/modal/modal";
import { useAdditionalServiceFilesQuery } from "@services/additional-services/queries";
import { useGetBillFileMutation } from "@services/billFiles/mutations";
import {
  useGetBillCertificateMutation,
  useUpdateBillSelectionMutation,
} from "@services/bills/mutations";
import {
  PeriodState,
  type IBillData,
  type IOverviewJointBillService,
} from "@services/overview/types";
import { useRestoreServiceProviderMutation } from "@services/serviceProviders/mutations";
import { downloadFile, isNullOrUndefined, openPdfPreview } from "@utils/common";
import { LT_MAX_PLANS } from "@utils/plans/constants";
import { COUNTRY_CODE } from "@/constants";
import { CountryCode } from "@/i18n";
import { DueDateBadge } from "./components/DueDateBadge/DueDateBadge";
import { JointBillServiceProviderBadge } from "./components/JointBillServiceProviderBadge/JointBillServiceProviderBadge";
import { useTemporaryProviderBillNotification } from "./useTemporaryProviderBillNotification";

interface IProviderBillProps {
  jointBillServiceId: IOverviewJointBillService["jointBillServiceId"];
  periodId: number;
  billData: IBillData;
  billsCount: number;
  providerBillPlanName: string;
  periodState: PeriodState;
}

export const ProviderBill: React.FC<IProviderBillProps> = ({
  jointBillServiceId,
  periodId,
  billData,
  billsCount,
  providerBillPlanName,
  periodState,
}) => {
  const { isDueDateEnabled, isChubbInsuranceEnabled } = useFeatureConfig();
  const { create } = useModalStore();
  const {
    billId,
    displayName,
    billStateId,
    totalSum,
    totalAmount,
    currencyId,
    isSelected,
    selectionStatusCanBeChanged,
    expectedPaymentIsEditable,
    billDetails,
    canServiceProviderBeRemoved,
    canEditServiceProviderCustomerCode,
    canEditServiceProviderDisplayName,
    isJointBillServiceProvider,
    jointBillServiceAgreementId,
    mustEnterBillNumber,
    serviceProviderId,
    additionalServiceFileExists,
  } = billData;
  const { isSummaryMode, jointBillServiceProviderBillPlanId } =
    useContext(JointBillContext);

  const { t } = useTranslation();
  const { toastApiError } = useApiErrorUtils();

  const {
    refetch: downloadAdditionalServiceFiles,
    isLoading: isAdditionalServiceFilesLoading,
  } = useAdditionalServiceFilesQuery(serviceProviderId, jointBillServiceId);

  const { mutateAsync: getBillFile, isPending: isBillFilePending } =
    useGetBillFileMutation();

  const {
    mutateAsync: getBillCertificate,
    isPending: isBillCertificateLoading,
  } = useGetBillCertificateMutation();

  const isEditEnabled =
    canEditServiceProviderCustomerCode || canEditServiceProviderDisplayName;

  const isBillFileAvailable = !!billData.hasBillFile;

  const billDetailIds = billDetails?.map((d) => d.billDetailId);
  const meterReadings = billDetails?.sort((a, b) => a.sortOrder - b.sortOrder);

  const billDisplayName = isJointBillServiceProvider
    ? t("bill.providerDisplayName", { planName: providerBillPlanName })
    : displayName;

  const { mutate: updateBillSelection, isPending: isBillSelectionLoading } =
    useUpdateBillSelectionMutation({
      jointBillServiceId,
      periodId,
    });

  const { mutateAsync: restoreProviderBill } =
    useRestoreServiceProviderMutation(
      jointBillServiceId,
      jointBillServiceAgreementId,
    );

  const onRestoreProviderBillClick = async () => {
    const response = await restoreProviderBill().catch(toastApiError);

    if (response) {
      toast.success(t("bill.interactions.providerWillBeRemoved.toast.success"));
    }
  };

  const openServiceProviderRemovalModal = useCallback(() => {
    create((modalProps) => (
      <RemoveServiceProviderModal
        {...modalProps}
        jointBillServiceId={jointBillServiceId}
        billData={billData}
      />
    ));
  }, [billData, create, jointBillServiceId]);

  const openServiceProviderEditModal = useCallback(() => {
    create((modalProps) => (
      <EditServiceProviderSettingsModal
        {...modalProps}
        jointBillServiceId={jointBillServiceId}
        billData={billData}
        billsCount={billsCount}
      />
    ));
  }, [billData, billsCount, create, jointBillServiceId]);

  const onProviderBillClick = useCallback(() => {
    create((modalProps) => (
      <BillDetailizationModal
        {...modalProps}
        billId={billData.billId}
        billDetailizationUrl={billData.billDetailizationUrl}
      />
    ));
  }, [billData.billDetailizationUrl, billData.billId, create]);

  const onProviderBookletClick = useCallback(() => {
    create((modalProps) => (
      <BillBookletModal
        {...modalProps}
        billBookletFileUrl={billData.billBookletFileUrl}
      />
    ));
  }, [billData.billBookletFileUrl, create]);

  const onBillCarryOverClick = useCallback(() => {
    create((modalProps) => (
      <BillCarryOverModal
        {...modalProps}
        billId={billId}
        jointBillServiceAgreementId={billData.jointBillServiceAgreementId}
        jointBillServiceId={jointBillServiceId}
      />
    ));
  }, [
    create,
    billId,
    jointBillServiceId,
    billData.jointBillServiceAgreementId,
  ]);

  const openTransferServiceProviderModal = useCallback(() => {
    create((modalProps) => (
      <TransferServiceProviderModal
        billData={billData}
        jointBillServiceId={jointBillServiceId}
        {...modalProps}
      />
    ));
  }, [create, billData, jointBillServiceId]);

  const openPdf = useCallback(async () => {
    const response = await getBillFile({
      billId: billData.billId,
      jointBillServiceId: jointBillServiceId,
    });

    openPdfPreview(response.fileName, response.data);
  }, [billData.billId, getBillFile, jointBillServiceId]);

  const openChubbInsurance = async () => {
    await downloadAdditionalServiceFiles()
      .then((response) => {
        if (response.data?.fileName.endsWith("pdf")) {
          openPdfPreview(response.data.fileName, response.data.data);
        } else if (response.data) {
          downloadFile(
            response.data,
            response.data.fileName.endsWith("zip")
              ? "application/zip"
              : undefined,
          );
        }
      })
      .catch(toastApiError);
  };

  const downloadBillCertificate = () => {
    toast.promise(
      getBillCertificate({
        jointBillServiceId,
        jointBillServiceCertificateId:
          billData?.certificate?.jointBillServiceCertificateId ?? 0,
      }),
      {
        loading: t("bill.interactions.downloadCertFile.loading"),
        success: (file) => {
          if (!isNullOrUndefined(file?.data)) {
            downloadFile(file);
          }
          return t("bill.interactions.downloadCertFile.success");
        },
        error: t("bill.interactions.downloadCertFile.error"),
      },
    );
  };

  const { Notification } = useTemporaryProviderBillNotification(billData);

  const isInsuranceEnabled =
    isJointBillServiceProvider &&
    isChubbInsuranceEnabled &&
    (COUNTRY_CODE !== CountryCode.LT ||
      (jointBillServiceProviderBillPlanId != null &&
        LT_MAX_PLANS.has(jointBillServiceProviderBillPlanId)));

  return (
    <JointBillTableRowLayout
      nameCell={
        <ProviderBillNameCell
          name={billDisplayName}
          statusBadge={<ProviderBillStatusBadge billState={billStateId} />}
          isProviderBillButtonEnabled={!!billData.billDetailizationUrl}
          onProviderBillClick={onProviderBillClick}
          isProviderBookletButtonEnabled={!!billData.billBookletFileUrl}
          onProviderBookletClick={onProviderBookletClick}
          shouldDisplayRemoveButton={!isJointBillServiceProvider}
          isRemoveProviderEnabled={canServiceProviderBeRemoved}
          onRemoveProviderClick={openServiceProviderRemovalModal}
          removeButtonTooltipContent={
            canServiceProviderBeRemoved
              ? t("bill.interactions.removeProvider.tooltip")
              : billData.isAgreementUnregistering
                ? t("bill.interactions.providerWillBeRemoved.tooltip")
                : t("bill.interactions.removeProvider.disabledTooltip")
          }
          isEditProviderEnabled={isEditEnabled}
          onEditProviderClick={openServiceProviderEditModal}
          shouldDisplayRestoreActions={billData.isAgreementUnregistering}
          onRestoreProviderClick={onRestoreProviderBillClick}
          isBillCarryOverEnabled={billData.canTransferPreviousExpectedPayment}
          onBillCarryOverClick={onBillCarryOverClick}
          providerBadge={
            billData.isJointBillServiceProvider ? (
              <JointBillServiceProviderBadge />
            ) : isDueDateEnabled ? (
              <DueDateBadge billData={billData} />
            ) : undefined
          }
          isBillFileAvailable={isBillFileAvailable}
          isBillFileLoading={isBillFilePending}
          onBillFileClick={openPdf}
          isInsuranceEnabled={isInsuranceEnabled}
          isInsuranceAvailable={additionalServiceFileExists}
          isInsuranceFilesLoading={isAdditionalServiceFilesLoading}
          onInsuranceButtonClick={openChubbInsurance}
          isCertFileAvailable={!!billData?.certificate}
          isCertFileLoading={isBillCertificateLoading}
          onCertFileClick={downloadBillCertificate}
          isTransferAvailable={billData.isTransferable}
          onTransferClick={openTransferServiceProviderModal}
        />
      }
      statusCell={<ProviderBillStatusBadge billState={billStateId} />}
      totalCell={
        <ProviderBillTotalCell
          jointBillServiceId={jointBillServiceId}
          periodId={periodId}
          totalSum={totalSum}
          totalAmount={totalAmount}
          currency={currencyId}
          isCheckboxChecked={isSelected}
          isCheckboxDisabled={!selectionStatusCanBeChanged}
          isCheckboxLoading={isBillSelectionLoading}
          onCheckboxClick={() =>
            updateBillSelection({
              billId,
              isSelected: !isSelected,
            })
          }
          billId={billId}
          isTotalAmountEditable={expectedPaymentIsEditable}
          isProviderBillButtonEnabled={!!billData.billDetailizationUrl}
          onProviderBillClick={onProviderBillClick}
          isProviderBookletButtonEnabled={!!billData.billBookletFileUrl}
          onProviderBookletClick={onProviderBookletClick}
          isProviderEditButtonEnabled={!isJointBillServiceProvider}
          isProviderRemoveButtonEnabled={!isJointBillServiceProvider}
          onRemoveProviderClick={openServiceProviderRemovalModal}
          onEditProviderClick={openServiceProviderEditModal}
          isBillFileAvailable={isBillFileAvailable}
          isBillFileLoading={isBillFilePending}
          onBillFileClick={openPdf}
          isBillCarryOverEnabled={billData.canTransferPreviousExpectedPayment}
          onBillCarryOverClick={onBillCarryOverClick}
          isChubbBillInsuranceEnabled={isJointBillServiceProvider}
          isChubbBillInsuranceAvailable={additionalServiceFileExists}
          isChubbInsuranceFilesLoading={isAdditionalServiceFilesLoading}
          onChubbInsuranceClick={openChubbInsurance}
          isCertFileAvailable={!!billData?.certificate}
          isCertFileLoading={isBillCertificateLoading}
          onCertFileClick={downloadBillCertificate}
          isTransferAvailable={billData.isTransferable}
          onTransferClick={openTransferServiceProviderModal}
        />
      }
      notification={Notification ? <Notification /> : undefined}
      expectedAmount={
        !isNullOrUndefined(totalSum) && totalSum !== totalAmount ? (
          <CalculatedAmount totalSum={totalSum} currency={currencyId} />
        ) : null
      }
      {...(!isSummaryMode && meterReadings?.length
        ? {
            billDetails: (
              <BillDetails
                jointBillServiceId={jointBillServiceId}
                jointBillServiceAgreementId={jointBillServiceAgreementId}
                periodId={periodId}
                billDetails={meterReadings}
                currency={currencyId}
                isBillDataExpectedPaymentEditable={expectedPaymentIsEditable}
              />
            ),
            billDetailsDefaultOpened:
              meterReadings.findIndex((m) => m.isMeterReading) > -1 &&
              periodState !== PeriodState.Paid &&
              periodState !== PeriodState.PartiallyPaid,
          }
        : {})}
      billDetailIds={billDetailIds}
      warnings={
        !isSummaryMode && mustEnterBillNumber ? (
          <BillNumberForm
            jointBillServiceId={jointBillServiceId}
            periodId={periodId}
            billId={billId}
          />
        ) : undefined
      }
    />
  );
};
